<template>
  <main
    class="d-flex flex-column justify-content-center align-items-center position-relative min-vh-100 pt-12"
  >
    <strapi-image
      class="register__background"
      :image="backgroundImage"
      as-background
    />

    <nuxt-link :to="`/${locale}`" :aria-label="t('app/home')">
      <common-gportal-logo :width="240" :height="30" />
    </nuxt-link>

    <div class="register__form w-100 mt-8 rounded py-2 px-4 px-sm-8">
      <div class="row">
        <div class="col-6 py-1">
          <nuxt-link :to="`/${locale}`" class="text-decoration-none fs-tiny">
            <fa-icon :icon="faArrowLeft" class="me-1" />
            {{ t('register/page/back_to_homepage') }}
          </nuxt-link>
        </div>

        <div class="col-6 col-sm-3 offset-sm-3">
          <form-dropdown
            :model-value="currentLocale"
            :items="availableLocales"
            @update:model-value="changeLocale"
          >
            <fa-icon class="text-rhino" :icon="faLocationDot" />
            <span class="register__form-dropdown-label fw-normal">{{
              currentLocale.displayLanguage
            }}</span>
          </form-dropdown>
        </div>
      </div>

      <h1 class="mt-6 fs-2 text-center">
        {{ t('register/page/header/create_account') }}
      </h1>

      <p class="text-center">
        {{ t('app/do-you-already-have-an-account') }}
        <a
          href="#"
          class="text-decoration-none"
          @click.prevent="() => login(`/${locale}`)"
        >
          {{ t('app/login-here') }}
        </a>
      </p>

      <p class="mb-2">
        <strong class="fs-2">
          {{ t('register/page/header/register_with') }}
        </strong>
      </p>

      <div class="d-flex gap-4">
        <button
          class="register__provider-button register__provider-button--facebook btn rounded-circle"
          :title="
            t('frontend/register-with-provider', { provider: 'Facebook' })
          "
          type="button"
          @click="registerThirdparty('facebook')"
        >
          <fa-icon class="text-white" :icon="faFacebookF" />
        </button>

        <button
          class="register__provider-button register__provider-button--google btn rounded-circle"
          :title="t('frontend/register-with-provider', { provider: 'Google' })"
          type="button"
          @click="registerThirdparty('google')"
        >
          <fa-icon class="text-white" :icon="faGoogle" />
        </button>

        <button
          class="register__provider-button register__provider-button--twitch btn rounded-circle"
          :title="t('frontend/register-with-provider', { provider: 'Twitch' })"
          type="button"
          @click="registerThirdparty('twitch')"
        >
          <fa-icon class="text-white" :icon="faTwitch" />
        </button>

        <button
          class="register__provider-button register__provider-button--discord btn rounded-circle"
          :title="t('frontend/register-with-provider', { provider: 'Discord' })"
          type="button"
          @click="registerThirdparty('discord')"
        >
          <fa-icon class="text-white" :icon="faDiscord" />
        </button>

        <button
          class="register__provider-button register__provider-button--xbox btn rounded-circle"
          :title="
            t('frontend/register-with-provider', { provider: 'Xbox Live' })
          "
          type="button"
          @click="registerThirdparty('microsoft')"
        >
          <fa-icon class="text-white" :icon="faXbox" />
        </button>
      </div>

      <div class="register__divider mt-4">
        <span class="text-center mx-2">
          {{ t('register/page/header/or_register_with') }}
        </span>
      </div>

      <register-form />
    </div>
  </main>
</template>

<script lang="ts" setup>
import {
  faFacebookF,
  faGoogle,
  faTwitch,
  faXbox,
  faDiscord,
} from '@fortawesome/free-brands-svg-icons';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import type { StrapiImage } from '~/apollo/types/types';
import useAuth from '~/composables/useAuth';
import type { GPLocaleObject } from '~/locales/types';
import type SelectItem from '../form/dropdown/types';

const { localeProperties, locale, setLocale, t, locales } = useI18n();
const enabledLocales = ['de', 'en'];
const availableLocales = enabledLocales.map<SelectItem>((key) => ({
  key,
  label:
    (locales.value as GPLocaleObject[]).find((loc) => loc.code === key)
      ?.displayLanguage || key,
}));

const backgroundImage = {
  url: 'https://ccdn.g-portal.com/register_bg_1133b4d12e.png',
  alternativeText: 'Register Background',
  width: 1600,
  height: 900,
} as StrapiImage;

const currentLocale = ref<GPLocaleObject>(
  localeProperties.value as GPLocaleObject,
);

if (enabledLocales.indexOf(locale.value) < 0) {
  setLocale('en');
}

const changeLocale = (loc: { key: string }) => {
  setLocale(loc.key);
};

const { login } = useAuth();
const registerThirdparty = (provider: string) => {
  login(`/${locale.value}`, provider);
};

useHead({
  title: `${t('frontend/register').toString()} | GPORTAL`,
});

useSeoMeta({
  title: `${t('frontend/register').toString()} | GPORTAL`,
});
</script>

<style scoped lang="scss">
@import 'gportal-theme/scss/colors';

.register {
  &__background {
    mask-image: linear-gradient(to bottom, black, black 90%, transparent 99%);
    background-position: center;
    object-position: center;
    height: 100vh;
  }

  &__form {
    background: $gradient-background;
    max-width: 37.5rem;

    &-dropdown-label {
      letter-spacing: normal;
    }

    &-select {
      .filterinput__select {
        margin-left: -1rem;
        margin-right: -1rem;
      }
    }
  }

  &__divider {
    display: flex;

    &:before,
    &:after {
      content: '';
      display: block;
      flex: 1;
      border-top: $gp-mouse 1px solid;
      margin-top: 0.75rem;
    }
  }

  &__provider-button {
    width: 2.5rem;
    height: 2.5rem;

    &--facebook {
      background-color: #1c74e4;
    }

    &--google {
      background-color: #de5246;
    }

    &--twitch {
      background-color: #9147f9;
    }

    &--xbox {
      background-color: #107c1a;
    }

    &--discord {
      background-color: #5865f2;
    }
  }
}

:deep(.register__form-select) {
  .input-select {
    &--open {
      margin-left: -1rem;
      margin-right: -1rem;
    }

    &__value {
      padding: 0 !important;
    }
  }
}
</style>
